// =====================================================================
// src/main.js — a tela: liga o DOM aos módulos puros
//
// Tudo que é DECISÃO mora em src/lib/ (contexto.js, auth.js, icones.js) e é
// testado em node. Este arquivo só faz o que exige navegador: ler campos,
// trocar seções, desenhar o menu, abrir o menu no celular, alternar o tema.
// Se algo aqui parecer lógica, é sinal de que devia descer para lib/.
//
// O desenho é o do app do Monteiro de Moraes (sidebar navy, cards, botões,
// menu que desliza no celular, modo escuro por filtro). O que NÃO veio de
// lá: o JS embutido de 25 mil linhas — cada módulo entra aqui como um
// arquivo em src/, e o index.html não tem lógica.
//
// Configuração pública (URL do projeto e chave anon) entra em tempo de
// construção, por `define` do esbuild, a partir do .env — veja
// scripts/construir.js. Nenhum segredo passa por aqui.
// =====================================================================
import { criarAuth } from './lib/auth.js';
import { ESTADOS, estadoDaTela, motivoSemAcesso, textoDefinirSenha, slugDoHost, abas, tituloDoContexto, avisoDeSubdominio, moduloDoHash, legendaDoModulo } from './lib/contexto.js';
import { iconeDoModulo } from './lib/icones.js';
import { errosDoAceite, introducao, unidadesTexto, formatarDocumento, documentoValido, cadastroSemDocumento } from './lib/termo.js';
import { fecharModal } from './ui.js';
import { montarTopo } from './topo.js';
import { montar as montarGovernanca } from './modulos/governanca.js';
import { montar as montarEscalas } from './modulos/escalas.js';
import { montar as montarUsuarios } from './modulos/usuarios.js';
import { montar as montarConfiguracoes } from './modulos/configuracoes.js';
import { montar as montarReservas } from './modulos/reservas.js';
import { montar as montarHospedes } from './modulos/hospedes.js';
import { montar as montarCheckin } from './modulos/checkin.js';
import { montar as montarPrecheckin } from './modulos/precheckin.js';
import { montar as montarPainel } from './modulos/painel.js';
import { montar as montarAuditoria } from './modulos/auditoria.js';
import { montar as montarManutencao } from './modulos/manutencao.js';
import { montar as montarGaragem } from './modulos/garagem.js';
import { montar as montarVisitas } from './modulos/visitas.js';
import { legendaDe as legendaGaragem } from './lib/garagem.js';
import { legendaDe as legendaVisitas } from './lib/visitas.js';
import { legendaDe as legendaReservas, visaoDe as visaoDeReservas } from './lib/reservas.js';
import { montar as montarDashboard } from './modulos/dashboard.js';
import { montar as montarFinanceiro } from './modulos/financeiro.js';
import { montar as montarImportacao } from './modulos/importacao.js';
import { montar as montarPessoal } from './modulos/pessoal.js';
import { montar as montarFrigobar } from './modulos/frigobar.js';
import { montar as montarSeguranca } from './modulos/seguranca.js';
import { montar as montarFnrh } from './modulos/fnrh.js';
import { montar as montarFlats } from './modulos/flats.js';

// Cada módulo construído entra aqui: chave do catálogo → montar(cont, deps),
// que devolve a função de desmontar. Módulo sem entrada mostra o aviso
// "ainda não construído".
// A legenda debaixo do título, por módulo (lib/contexto.js: legendaDoModulo).
// Módulo sem entrada aqui usa a do nível.
// 0101: `vaga_garagem` é a aba Garagem da imobiliária — a mesma tela, na
// visão dela (lib/garagem.js), com a mesma legenda.
const LEGENDAS = { garagem: legendaGaragem, vaga_garagem: legendaGaragem, visitas: legendaVisitas, reservas: legendaReservas };
const MODULOS = { governanca: montarGovernanca, escalas: montarEscalas, usuarios: montarUsuarios, configuracoes: montarConfiguracoes, reservas: montarReservas, hospedes: montarHospedes, checkin: montarCheckin, precheckin: montarPrecheckin, painel: montarPainel, auditoria: montarAuditoria, manutencao: montarManutencao, escalonamento: montarManutencao, garagem: montarGaragem, visitas: montarVisitas, financeiro: montarFinanceiro, importacao: montarImportacao, pessoal: montarPessoal, frigobar: montarFrigobar, seguranca: montarSeguranca, fnrh: montarFnrh, flats: montarFlats, vaga_garagem: montarGaragem };
let desmontarAtual = null;

/* global __SUPABASE_URL__, __SUPABASE_ANON_KEY__, __VERSAO__ */
const auth = criarAuth({
  url: __SUPABASE_URL__,
  chave: __SUPABASE_ANON_KEY__,
  fetch: (u, o) => window.fetch(u, o),
  storage: window.localStorage,
  agora: () => Date.now(),
});

const $ = id => document.getElementById(id);
const telas = { [ESTADOS.ENTRAR]: 'tela-entrar', [ESTADOS.DEFINIR_SENHA]: 'tela-definir-senha', [ESTADOS.SEM_ACESSO]: 'tela-sem-acesso', [ESTADOS.TERMO]: 'tela-termo', [ESTADOS.APP]: 'tela-app' };
let contextoAtual = null;
// O link do e-mail (convite/redefinição) traz a sessão no hash. Se veio,
// a senha nova é a primeira coisa; o tipo decide o texto da tela.
let tipoDoLink = null;
{
  const t = auth.aceitarLink(location.hash);
  if (t) { tipoDoLink = t; history.replaceState(null, '', location.pathname + location.search); }
}

function mostrar(estado) {
  for (const [e, id] of Object.entries(telas)) $(id).hidden = (e !== estado);
  document.body.dataset.estado = estado;   // a prova por navegador lê isto
}

function erroEntrar(msg) {
  const el = $('entrar-erro');
  el.textContent = msg || '';
  el.classList.toggle('hidden', !msg);
}

function icone(nome, cls = 'ic') {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', cls);
  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
  use.setAttribute('href', '#' + nome);
  svg.appendChild(use);
  return svg;
}

// ── o app ─────────────────────────────────────────────────────────────
let topo = null;   // os botões do canto, montados uma vez ao entrar

function desenharApp(contexto, sessao) {
  contextoAtual = contexto;
  const titulo = tituloDoContexto(contexto);
  const [nome, ...resto] = titulo.split(' · ');
  $('app-quem').textContent = nome || '';
  $('app-quem-sub').textContent = resto.join(' · ');

  const aviso = avisoDeSubdominio(slugDoHost(location.hostname), contexto);
  $('app-aviso').hidden = !aviso;
  $('app-aviso').textContent = aviso || '';

  const nav = $('app-abas');
  nav.replaceChildren();
  const lista = abas(contexto);
  for (const m of lista) {
    const a = document.createElement('a');
    a.className = 'nav-item';
    a.href = '#' + m.chave;
    a.dataset.modulo = m.chave;
    a.dataset.nivel = m.nivel;
    a.appendChild(icone(iconeDoModulo(m.chave)));
    a.appendChild(document.createTextNode(m.nome));
    a.title = m.nivel === 'editar' ? `${m.nome} — ver e editar` : `${m.nome} — somente leitura`;
    a.addEventListener('click', fecharMenuMobile);
    nav.appendChild(a);
  }
  if (lista.length === 0) {
    const p = document.createElement('div'); p.className = 'nav-vazio';
    p.textContent = 'Nenhum módulo liberado para o seu papel.';
    nav.appendChild(p);
  }
  marcarAbaAtual();
  $('rodape').textContent = `${sessao.usuario ? sessao.usuario.email : ''} · versão ${__VERSAO__}`;
}

// A aba atual vem do hash (#reservas). Ainda não há conteúdo de módulo:
// este é o esqueleto. Cada módulo entra depois como um arquivo em src/.
function marcarAbaAtual() {
  const atual = moduloDoHash(location.hash);
  let ativa = null;
  for (const a of document.querySelectorAll('#app-abas a')) {
    const e = a.dataset.modulo === atual;
    a.classList.toggle('active', e);
    if (e) { a.setAttribute('aria-current', 'page'); ativa = a; } else a.removeAttribute('aria-current');
  }
  const mod = ativa && contextoAtual ? abas(contextoAtual).find(m => m.chave === atual) : null;
  // O Início da IMOBILIÁRIA é o Dashboard do MdM (`renderDashboard`,
  // 2386): a casa dela abre nos números dela (0101: o login de dono saiu). O
  // hotel continua com "Escolha um módulo" — o painel dele é o Painel.
  const dono = !mod && !atual && visaoDeReservas(contextoAtual) === 'dono';
  $('pagina-titulo').textContent = mod ? mod.nome : (dono ? 'Dashboard' : 'Início');
  $('pagina-sub').textContent = dono ? 'Seus apartamentos' : legendaDoModulo(mod, contextoAtual, LEGENDAS);
  const cont = $('app-conteudo');
  if (desmontarAtual) { desmontarAtual(); desmontarAtual = null; }
  fecharModal();
  cont.replaceChildren();
  delete cont.dataset.tela;
  if (mod && MODULOS[mod.chave]) {
    desmontarAtual = MODULOS[mod.chave](cont, { auth, contexto: contextoAtual });
    return;
  }
  if (dono) { desmontarAtual = montarDashboard(cont, { auth, contexto: contextoAtual }); return; }
  const p = document.createElement('p'); p.className = 'vazio';
  p.textContent = mod ? `${mod.nome} — módulo ainda não construído.` : 'Escolha um módulo no menu.';
  cont.appendChild(p);
}

async function entrarNoApp() {
  const sessao = await auth.sessao();
  if (!sessao) { mostrar(ESTADOS.ENTRAR); return; }
  let contexto = null;
  // Veio pelo link do e-mail: a senha nova vem antes de meu_contexto().
  if (tipoDoLink) {
    const t = textoDefinirSenha(tipoDoLink);
    $('definir-titulo').textContent = t.titulo; $('definir-texto').textContent = t.texto;
    mostrar(estadoDaTela(sessao, null, true));
    return;
  }
  try { contexto = await auth.rpc('meu_contexto', {}); }
  catch (e) {
    // 401 já apagou a sessão dentro do adaptador; qualquer outro erro é rede.
    mostrar(ESTADOS.ENTRAR);
    erroEntrar((await auth.sessao()) ? 'Não foi possível carregar o seu acesso. Tente de novo.' : 'Sua sessão expirou. Entre de novo.');
    return;
  }
  const estado = estadoDaTela(sessao, contexto, false);
  if (estado === ESTADOS.TERMO) { await mostrarTermo(sessao); return; }
  if (estado === ESTADOS.SEM_ACESSO) {
    const m = motivoSemAcesso(contexto);
    $('sem-acesso-titulo').textContent = m.titulo;
    $('sem-acesso-texto').textContent = m.texto;
    $('sem-acesso-email').textContent = sessao.usuario ? sessao.usuario.email : '';
  }
  if (estado === ESTADOS.APP) desenharApp(contexto, sessao);
  mostrar(estado);
  // Os dois botões do canto só existem DENTRO do app: na tela de entrar
  // não há a quem avisar, e o tema de quem não entrou não é assunto nosso.
  //
  // `atualizar()` roda em TODA entrada, não só na primeira. Sem isso o
  // sino guardava a contagem de quem saiu, e a próxima pessoa a entrar
  // via o número da anterior — a prova de navegador pegou isso, e num app
  // multi-tenant é exatamente o vazamento que não se releva.
  if (estado === ESTADOS.APP) {
    topo = topo || montarTopo({ auth });
    if (topo) topo.atualizar();
  }
}

// ── 0072: o Termo de Adesão ───────────────────────────────────────────
// O banco já travou este login (app.eu); a tela só mostra o Termo, colhe o
// aceite e volta a pedir o contexto. Se, entre uma coisa e outra, o Termo
// deixou de ser devido (o hotel desligou, o login foi desativado), o
// banco devolve nulo e a pessoa cai na tela de sempre.
let termoAtual = null;
function erroTermo(msg) { const e = $('termo-erro'); e.textContent = msg || ''; e.classList.toggle('hidden', !msg); }
async function mostrarTermo(sessao) {
  try { termoAtual = await auth.rpc('termo_para_aceite', {}); } catch (e) { termoAtual = null; }
  if (!termoAtual) {
    const m = motivoSemAcesso(null);
    $('sem-acesso-titulo').textContent = m.titulo; $('sem-acesso-texto').textContent = m.texto;
    $('sem-acesso-email').textContent = sessao.usuario ? sessao.usuario.email : '';
    mostrar(ESTADOS.SEM_ACESSO);
    return;
  }
  const t = termoAtual, ad = t.aderente || {};
  $('termo-intro').textContent = introducao(t);
  $('termo-texto').textContent = t.texto || '';
  $('termo-texto').scrollTop = 0;
  $('termo-nome').value = ad.nome || '';
  $('termo-doc').value = ad.documento ? formatarDocumento(ad.documento) : '';
  // 0073 (2B): o documento é o do cadastro — só para ler. Sem documento
  // válido no cadastro, a tela já diz quem corrige e não deixa enviar.
  $('termo-doc').readOnly = true;
  const semDoc = !documentoValido(ad.documento || '');
  $('btn-termo-aceitar').disabled = semDoc;
  $('termo-unidades').textContent = unidadesTexto(ad.unidades);
  $('termo-li').checked = false;
  $('termo-li-texto').textContent = `Li e aceito o Termo de Adesão e Condições de Uso (versão ${t.versao}).`;
  erroTermo(semDoc ? cadastroSemDocumento(ad.nome) : '');
  mostrar(ESTADOS.TERMO);
}
$('form-termo').addEventListener('submit', async ev => {
  ev.preventDefault();
  const ad = (termoAtual && termoAtual.aderente) || {};
  const dados = { nome: $('termo-nome').value, documento: $('termo-doc').value, li: $('termo-li').checked, cadastro: ad.documento || '', aderente: ad.nome };
  const erros = errosDoAceite(dados);
  if (erros.length) { erroTermo(erros.join(' · ')); return; }
  const btn = $('btn-termo-aceitar'); btn.disabled = true; erroTermo('');
  try {
    await auth.rpc('aceitar_termo', { p_termo: termoAtual.termo_id, p_nome: dados.nome, p_documento: dados.documento });
  } catch (e) {
    // o texto mudou com a página aberta: mostra a versão nova, com o aviso
    if (/atualizado/.test(e.message)) await mostrarTermo(await auth.sessao());
    erroTermo(e.message);
    btn.disabled = !documentoValido(dados.cadastro);
    return;
  }
  btn.disabled = false;
  await entrarNoApp();
});

// ── entrar / sair ─────────────────────────────────────────────────────
$('form-entrar').addEventListener('submit', async ev => {
  ev.preventDefault();
  const btn = $('btn-entrar'); btn.disabled = true; erroEntrar('');
  try {
    await auth.entrar($('email').value, $('senha').value);
    $('senha').value = '';
    await entrarNoApp();
  } catch (e) {
    erroEntrar(e.message);
  } finally { btn.disabled = false; }
});

// ── esqueci a senha ───────────────────────────────────────────────────
function mostrarEsqueci(sim) {
  $('form-entrar').hidden = sim; $('form-esqueci').hidden = !sim;
  $('esqueci-erro').classList.add('hidden'); $('esqueci-ok').classList.add('hidden');
  if (sim) { $('esqueci-email').value = $('email').value; $('esqueci-email').focus(); }
}
$('lnk-esqueci').addEventListener('click', ev => { ev.preventDefault(); mostrarEsqueci(true); });
$('lnk-voltar-entrar').addEventListener('click', ev => { ev.preventDefault(); mostrarEsqueci(false); });
$('form-esqueci').addEventListener('submit', async ev => {
  ev.preventDefault();
  const btn = $('btn-esqueci'); btn.disabled = true;
  $('esqueci-erro').classList.add('hidden'); $('esqueci-ok').classList.add('hidden');
  try {
    await auth.recuperar($('esqueci-email').value);
    $('esqueci-ok').textContent = 'Se este e-mail estiver cadastrado, o link chega em instantes. Confira também a caixa de spam.';
    $('esqueci-ok').classList.remove('hidden');
  } catch (e) {
    $('esqueci-erro').textContent = e.message; $('esqueci-erro').classList.remove('hidden');
  } finally { btn.disabled = false; }
});

// ── definir senha (convite / redefinição) ─────────────────────────────
$('form-definir').addEventListener('submit', async ev => {
  ev.preventDefault();
  const erro = $('definir-erro'); erro.classList.add('hidden');
  const s1 = $('senha-nova').value, s2 = $('senha-nova2').value;
  if (s1 !== s2) { erro.textContent = 'As duas senhas não são iguais.'; erro.classList.remove('hidden'); return; }
  const btn = $('btn-definir'); btn.disabled = true;
  try {
    await auth.definirSenha(s1);
    $('senha-nova').value = ''; $('senha-nova2').value = '';
    tipoDoLink = null;
    await entrarNoApp();
  } catch (e) {
    erro.textContent = e.message; erro.classList.remove('hidden');
    if (!(await auth.sessao())) { tipoDoLink = null; mostrar(ESTADOS.ENTRAR); erroEntrar(e.message); }
  } finally { btn.disabled = false; }
});

$('btn-olho').addEventListener('click', () => {
  const s = $('senha'); const mostra = s.type === 'password';
  s.type = mostra ? 'text' : 'password';
  $('btn-olho').replaceChildren(icone(mostra ? 'ic-eye-off' : 'ic-eye'));
  $('btn-olho').setAttribute('aria-label', mostra ? 'Ocultar senha' : 'Mostrar senha');
});

async function sair() {
  await auth.sair();
  tipoDoLink = null;
  if (desmontarAtual) { desmontarAtual(); desmontarAtual = null; }
  contextoAtual = null;
  $('email').value = ''; $('senha').value = ''; erroEntrar('');
  fecharMenuMobile();
  mostrar(ESTADOS.ENTRAR);
}
$('btn-sair').addEventListener('click', sair);
$('btn-sair-sem-acesso').addEventListener('click', sair);
$('btn-sair-termo').addEventListener('click', sair);
window.addEventListener('hashchange', () => { if (document.body.dataset.estado === ESTADOS.APP) marcarAbaAtual(); });

// ── menu no celular ───────────────────────────────────────────────────
function abrirMenuMobile()  { $('sidebar').classList.add('open');    $('mobile-overlay').classList.add('visible'); }
function fecharMenuMobile() { $('sidebar').classList.remove('open'); $('mobile-overlay').classList.remove('visible'); }
$('mobile-menu-btn').addEventListener('click', () => ($('sidebar').classList.contains('open') ? fecharMenuMobile() : abrirMenuMobile()));
$('mobile-overlay').addEventListener('click', fecharMenuMobile);

// ── tema (como no Monteiro: filtro no conteúdo, preferência guardada) ─
const TEMA = 'sgh.tema';
function aplicarTema() { let t = null; try { t = localStorage.getItem(TEMA); } catch (_) {} document.body.classList.toggle('dark-mode', t === 'escuro'); }
$('btn-tema').addEventListener('click', () => {
  const escuro = !document.body.classList.contains('dark-mode');
  try { localStorage.setItem(TEMA, escuro ? 'escuro' : 'claro'); } catch (_) {}
  aplicarTema();
});
aplicarTema();

entrarNoApp();
